import { useMemo, useState } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { Box, IconButton, InputAdornment } from "@material-ui/core";
import { VisibilityOutlined, VisibilityOffOutlined } from "@material-ui/icons";
import { Field, Form, Formik } from "formik";
import * as yup from "yup";
import { PendingAppButton, SimpleTextField } from "solveiq.designsystem";
import { ErrorLabel } from "components/ErrorLabel/ErrorLabel";
// import { clearSignUpError } from "slices/authSlice";
import styles from "./SignUpForm.module.css";
import { handleSocialSignUp } from "utils/handleProvider";
import { useLocation } from "react-router-dom";
import GoogleLogo from "assets/google-logo.png";
import FacebookLogo from "assets/facebook-logo.png";
import { reportFullStoryEvent, useKissMetrics } from "ui.common";

declare var withSocialAuth: string;
declare var withFacebookProvider: string;
declare var withGoogleProvider: string;

type SignUpFormProps = {
  submitError?: string;
  onSubmit: (submitValues: ISignUpSubmitModel) => Promise<void>;
};

interface ISignUpSubmitModel {
  name: string;
  email: string;
  password: string;
}

const socialAuth =
  (withSocialAuth === "true" && withFacebookProvider === "true") ||
  (withSocialAuth === "true" && withGoogleProvider === "true");


function SignUpForm({ onSubmit, submitError }: SignUpFormProps) {
  const kissMetrics = useKissMetrics();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const { formatMessage } = useIntl();
  const validationSchema = useMemo(
    () =>
      yup.object({
        email: yup
          .string()
          .required(
            formatMessage({
              id: "signup.form.error.emailRequired",
              defaultMessage: "Email is required",
            })
          )
          .email(
            formatMessage({
              id: "signup.form.error.emailInvalid",
              defaultMessage: "Invalid email address",
            })
          ),
        name: yup
          .string()
          .test(
            "has-first-name-and-last-name",
            (d) =>
              formatMessage({
                id: "signup.form.error.nameInvalid",
                defaultMessage: "First and Last names are required",
              }),
            (value) =>
              typeof value === "string"
                ? /([a-zA-Z]+\s?\b){2,}/g.test(value)
                : false
          )
          .required(
            formatMessage({
              id: "signup.form.error.nameRequired",
              defaultMessage: "Name is required",
            })
          ),
        password: yup.string().required(
          formatMessage({
            id: "signup.form.error.passwordRequired",
            defaultMessage: "Password is required",
          })
        ),
      }),
    [formatMessage]
  );

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleFacebook = () => {
    kissMetrics.report("Sign up with Facebook", []);
    reportFullStoryEvent("Sign up with Facebook");
    handleSocialSignUp("Facebook", location);
  };

  const handleGoogle = () => {
    kissMetrics.report("Sign up with Google", []);
    reportFullStoryEvent("Sign up with Google");
    handleSocialSignUp("Google", location);
  };

  return (
    <Formik
      initialValues={{
        email: "",
        name: "",
        password: "",
      }}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        await onSubmit({
          name: values.name,
          email: values.email,
          password: values.password,
        });
      }}
    >
      {({ handleSubmit, isSubmitting }) => (
        <Form
          noValidate
          className={styles.form}
          onSubmit={(e) => {
            // dispatch(clearSignUpError());
            handleSubmit(e);
          }}
        >
          {socialAuth ? (
            <>
              {withGoogleProvider === "true" ? (
                <Box mt="27px" mb="17px" className={styles.signupButtonContainer}>
                  <Box
                    className={styles.signUpExternalProviderBtn}
                    onClick={handleGoogle}
                  >
                    <img
                      src={GoogleLogo}
                      alt="google-logo"
                      className={styles.signUpExternalProviderBtnLogo}
                    />
                    <FormattedMessage
                      id="signup.form.signupButtonGoogle"
                      defaultMessage="Continue With Google"
                    />
                  </Box>
                </Box>
              ) : null}
              {withFacebookProvider === "true" ? (
                <Box mb="17px" className={styles.signupButtonContainer}>
                  <Box
                    className={styles.signUpExternalProviderBtn}
                    onClick={handleFacebook}
                  >
                    <img
                      src={FacebookLogo}
                      alt="google-logo"
                      className={styles.signUpExternalProviderBtnLogo}
                    />
                    <FormattedMessage
                      id="signup.form.signupButtonFacebook"
                      defaultMessage="Continue With Facebook"
                    />
                  </Box>
                </Box>
              ) : null}
              <Box className={styles.orDivider}>
                <FormattedMessage
                  id="signup.form.orDivider"
                  defaultMessage="Or"
                />
              </Box>
            </>
          ) : null}

          <div className={styles.fieldContainer}>
            <Box mb={3} display="flex">
              <Field
                className={styles.field}
                component={SimpleTextField}
                label={formatMessage({
                  id: "signup.form.name",
                  defaultMessage: "Full Name",
                })}
                name="name"
                type="text"
              />
            </Box>
            <Box mb={3} display="flex">
              <Field
                className={styles.field}
                component={SimpleTextField}
                label={formatMessage({
                  id: "signup.form.email",
                  defaultMessage: "Email Address",
                })}
                name="email"
                type="email"
              />
            </Box>
            <Box display="flex">
              <Field
                className={styles.field}
                component={SimpleTextField}
                label={formatMessage({
                  id: "signup.form.password",
                  defaultMessage: "Password",
                })}
                name="password"
                type={showPassword ? "text" : "password"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <span className={styles.passwordAdornment}>
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                        >
                          {showPassword ? (
                            <VisibilityOutlined />
                          ) : (
                            <VisibilityOffOutlined />
                          )}
                        </IconButton>
                        <span>
                          {showPassword
                            ? formatMessage({
                                id: "signup.form.password.hide",
                                defaultMessage: "Hide",
                              })
                            : formatMessage({
                                id: "signup.form.password.show",
                                defaultMessage: "Show",
                              })}
                        </span>
                      </span>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box alignSelf="center">
              <ErrorLabel>{submitError}</ErrorLabel>
            </Box>
          </div>
          <Box mt={5.5} className={styles.signupButtonContainer}>
            <PendingAppButton
              className={styles.signupButton}
              isPending={isSubmitting}
              type="submit"
            >
              <FormattedMessage
                id="signup.form.signupButton"
                defaultMessage="Submit"
              />
            </PendingAppButton>
          </Box>
        </Form>
      )}
    </Formik>
  );
}

export default SignUpForm;
